import { useState } from 'react';
import { ChevronDown, Snowflake } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { CHIP_CLASS, CarrierFeatureChip } from './CarrierFeatureChip';

interface TemperatureCoverageChipProps {
  label: string;
  /** Popover heading, e.g. "Temp-controlled in". */
  popoverTitle: string;
  /**
   * Every province this carrier cold-chains to. Cold-chain safety rule: the
   * popover lists them ALL — no "+N more" truncation, ever. A merchant who
   * cannot see a province in this list must be able to conclude it is not
   * covered.
   */
  provinceNames: string[];
}

export function TemperatureCoverageChip({
  label,
  popoverTitle,
  provinceNames,
}: TemperatureCoverageChipProps) {
  const [open, setOpen] = useState(false);

  // Nothing to disclose (no province data loaded yet) — degrade to a plain,
  // non-interactive chip rather than opening an empty popover.
  if (provinceNames.length === 0) {
    return <CarrierFeatureChip icon={Snowflake} label={label} />;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type='button'
          aria-expanded={open}
          className={cn(
            CHIP_CLASS,
            'cursor-pointer transition-colors',
            // Pressed state must stay neutral — `bg-sd-accent` is the Boxy
            // brand yellow, which made the open chip the loudest thing on the
            // page and out-shouted the amber restricted-items warning. It also
            // has to be *visible*: sd-secondary measured 1–4 units off the
            // closed fill, so the fill carried no affordance at all.
            open && 'border-sd-muted-foreground/40 bg-sd-border',
          )}
        >
          <Snowflake className='h-3.5 w-3.5 shrink-0 text-sd-muted-foreground' strokeWidth={2} />
          {label}
          {/* Tinted from the foreground, not `bg-sd-border` — that is the same
              token the OPEN chip fills with, so the pill used to disappear into
              the chip when pressed (measured contrast 1.000). This stays a step
              darker than the chip in both states. */}
          <span className='inline-flex h-[17px] min-w-[17px] items-center justify-center rounded-full bg-sd-muted-foreground/20 px-1 text-[11px] font-bold leading-none text-sd-muted-foreground'>
            {provinceNames.length}
          </span>
          <ChevronDown
            className={cn('h-3 w-3 shrink-0 transition-transform', open && 'rotate-180')}
            strokeWidth={2.5}
          />
        </button>
      </PopoverTrigger>

      <PopoverContent
        align='start'
        sideOffset={8}
        collisionPadding={12}
        className='w-auto max-w-[280px] rounded-xl border-sd-border bg-sd-card p-3.5 shadow-lg'
      >
        <div className='mb-2 text-[11px] font-semibold uppercase tracking-[0.04em] text-sd-muted-foreground'>
          {popoverTitle}
        </div>
        {/* The province list is never truncated, so bound the popover and let
            it scroll rather than letting a long list run off the viewport. */}
        <div className='flex max-h-[40vh] flex-wrap gap-2 overflow-y-auto'>
          {provinceNames.map((name, index) => (
            <span
              key={`${name}-${index}`}
              className='inline-flex items-center rounded-lg border border-sd-border bg-sd-muted px-[11px] py-[5px] text-[13px] font-medium text-sd-foreground'
            >
              {name}
            </span>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
